import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Firestore collection names
const SCHEDULES_COLLECTION = 'schedules';
const SCHEDULE_INSTANCES_COLLECTION = 'scheduleInstances';

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  return db && process.env.REACT_APP_FIREBASE_API_KEY && 
         process.env.REACT_APP_FIREBASE_API_KEY !== 'your_api_key_here';
};

export interface Schedule {
  id?: string;
  title: string;
  description?: string;
  lessonId: string;
  teacherId: string;
  className?: string;
  startDate: Date;
  endDate?: Date;
  startTime: string; // Format: "HH:MM"
  duration: number; // in minutes
  recurrence: RecurrencePattern;
  isActive: boolean;
  maxStudents?: number;
  location?: string;
  notes?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface RecurrencePattern {
  type: 'none' | 'daily' | 'weekly' | 'monthly';
  interval: number; // Every X days/weeks/months
  daysOfWeek?: number[]; // 0-6 (Sunday-Saturday) for weekly
  dayOfMonth?: number; // 1-31 for monthly
  endAfterOccurrences?: number;
  endDate?: Date;
}

export interface ScheduleInstance {
  id?: string;
  scheduleId: string;
  lessonId: string;
  teacherId: string;
  className?: string;
  startDateTime: Date;
  endDateTime: Date;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  actualStartTime?: Date;
  actualEndTime?: Date;
  attendance?: string[]; // Student IDs
  notes?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface ScheduleConflict {
  scheduleId: string;
  conflictingScheduleId: string;
  conflictType: 'time_overlap' | 'resource_conflict' | 'teacher_conflict';
  date: string;
}

class ScheduleService {
  // For backward compatibility when Firebase is not configured
  private fallbackSchedules: Schedule[] = [];
  private fallbackInstances: ScheduleInstance[] = [];

  // Generate unique IDs
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Create a new schedule
  async createSchedule(schedule: Omit<Schedule, 'id' | 'createdAt' | 'updatedAt'>): Promise<Schedule> {
    const newSchedule: Schedule = {
      ...schedule,
      id: this.generateId(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      if (isFirebaseConfigured()) {
        // Filter out undefined values before saving to Firestore
        const scheduleData: any = {
          title: newSchedule.title,
          description: newSchedule.description,
          lessonId: newSchedule.lessonId,
          teacherId: newSchedule.teacherId,
          startDate: Timestamp.fromDate(newSchedule.startDate),
          startTime: newSchedule.startTime,
          duration: newSchedule.duration,
          isActive: newSchedule.isActive,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };

        // Handle recurrence object - filter out undefined values
        const recurrenceData: any = {
          type: newSchedule.recurrence.type,
          interval: newSchedule.recurrence.interval
        };

        // Only add optional recurrence fields if they have values
        if (newSchedule.recurrence.daysOfWeek) recurrenceData.daysOfWeek = newSchedule.recurrence.daysOfWeek;
        if (newSchedule.recurrence.dayOfMonth) recurrenceData.dayOfMonth = newSchedule.recurrence.dayOfMonth;
        if (newSchedule.recurrence.endAfterOccurrences) recurrenceData.endAfterOccurrences = newSchedule.recurrence.endAfterOccurrences;
        if (newSchedule.recurrence.endDate) recurrenceData.endDate = Timestamp.fromDate(newSchedule.recurrence.endDate);

        scheduleData.recurrence = recurrenceData;

        // Only add optional fields if they have values
        if (newSchedule.className) scheduleData.className = newSchedule.className;
        if (newSchedule.endDate) scheduleData.endDate = Timestamp.fromDate(newSchedule.endDate);
        if (newSchedule.maxStudents) scheduleData.maxStudents = newSchedule.maxStudents;
        if (newSchedule.location) scheduleData.location = newSchedule.location;
        if (newSchedule.notes) scheduleData.notes = newSchedule.notes;

        const docRef = await addDoc(collection(db, SCHEDULES_COLLECTION), scheduleData);
        newSchedule.id = docRef.id;
      } else {
        this.fallbackSchedules.push(newSchedule);
      }

      // Generate schedule instances
      await this.generateScheduleInstances(newSchedule.id!);
      
      return newSchedule;
    } catch (error) {
      console.error('Error creating schedule:', error);
      throw new Error('Failed to create schedule');
    }
  }

  // Get all schedules for a teacher
  async getSchedulesByTeacher(teacherId: string): Promise<Schedule[]> {
    try {
      if (isFirebaseConfigured()) {
        const q = query(
          collection(db, SCHEDULES_COLLECTION),
          where('teacherId', '==', teacherId),
          orderBy('startDate', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          startDate: doc.data().startDate.toDate(),
          endDate: doc.data().endDate?.toDate(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        })) as Schedule[];
      } else {
        return this.fallbackSchedules.filter(s => s.teacherId === teacherId);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      return [];
    }
  }

  // Get all schedules for a class
  async getSchedulesByClass(className: string): Promise<Schedule[]> {
    try {
      if (isFirebaseConfigured()) {
        const q = query(
          collection(db, SCHEDULES_COLLECTION),
          where('className', '==', className),
          orderBy('startDate', 'desc')
        );
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          startDate: doc.data().startDate.toDate(),
          endDate: doc.data().endDate?.toDate(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        })) as Schedule[];
      } else {
        return this.fallbackSchedules.filter(s => s.className === className);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      return [];
    }
  }

  // Get schedule instances for a date range
  async getScheduleInstances(startDate: Date, endDate: Date, teacherId?: string): Promise<ScheduleInstance[]> {
    try {
      if (isFirebaseConfigured()) {
        let q = query(
          collection(db, SCHEDULE_INSTANCES_COLLECTION),
          where('startDateTime', '>=', Timestamp.fromDate(startDate)),
          where('startDateTime', '<=', Timestamp.fromDate(endDate)),
          orderBy('startDateTime', 'asc')
        );

        if (teacherId) {
          q = query(q, where('teacherId', '==', teacherId));
        }

        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          startDateTime: doc.data().startDateTime.toDate(),
          endDateTime: doc.data().endDateTime.toDate(),
          actualStartTime: doc.data().actualStartTime?.toDate(),
          actualEndTime: doc.data().actualEndTime?.toDate(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate()
        })) as ScheduleInstance[];
      } else {
        return this.fallbackInstances.filter(instance => {
          const instanceDate = instance.startDateTime;
          const isInRange = instanceDate >= startDate && instanceDate <= endDate;
          const matchesTeacher = !teacherId || instance.teacherId === teacherId;
          return isInRange && matchesTeacher;
        });
      }
    } catch (error) {
      console.error('Error fetching schedule instances:', error);
      return [];
    }
  }

  // Update a schedule
  async updateSchedule(scheduleId: string, updates: Partial<Schedule>): Promise<void> {
    try {
      if (isFirebaseConfigured()) {
        const docRef = doc(db, SCHEDULES_COLLECTION, scheduleId);
        
        // Filter out undefined values before updating Firestore
        const updateData: any = {
          updatedAt: Timestamp.now()
        };

        // Only include fields that are not undefined
        Object.entries(updates).forEach(([key, value]) => {
          if (value !== undefined) {
            if (key === 'startDate' && value instanceof Date) {
              updateData[key] = Timestamp.fromDate(value);
            } else if (key === 'endDate' && value instanceof Date) {
              updateData[key] = Timestamp.fromDate(value);
            } else if (key === 'recurrence' && typeof value === 'object' && value !== null) {
              // Handle recurrence object - filter out undefined values
              const recurrenceValue = value as RecurrencePattern;
              const recurrenceData: any = {
                type: recurrenceValue.type,
                interval: recurrenceValue.interval
              };

              // Only add optional recurrence fields if they have values
              if (recurrenceValue.daysOfWeek) recurrenceData.daysOfWeek = recurrenceValue.daysOfWeek;
              if (recurrenceValue.dayOfMonth) recurrenceData.dayOfMonth = recurrenceValue.dayOfMonth;
              if (recurrenceValue.endAfterOccurrences) recurrenceData.endAfterOccurrences = recurrenceValue.endAfterOccurrences;
              if (recurrenceValue.endDate) recurrenceData.endDate = Timestamp.fromDate(recurrenceValue.endDate);

              updateData[key] = recurrenceData;
            } else {
              updateData[key] = value;
            }
          }
        });

        await updateDoc(docRef, updateData);
      } else {
        const index = this.fallbackSchedules.findIndex(s => s.id === scheduleId);
        if (index !== -1) {
          this.fallbackSchedules[index] = {
            ...this.fallbackSchedules[index],
            ...updates,
            updatedAt: new Date()
          };
        }
      }

      // Regenerate instances if recurrence changed
      if (updates.recurrence || updates.startDate || updates.endDate) {
        await this.regenerateScheduleInstances(scheduleId);
      }
    } catch (error) {
      console.error('Error updating schedule:', error);
      throw new Error('Failed to update schedule');
    }
  }

  // Delete a schedule
  async deleteSchedule(scheduleId: string): Promise<void> {
    try {
      if (isFirebaseConfigured()) {
        // Delete the schedule
        await deleteDoc(doc(db, SCHEDULES_COLLECTION, scheduleId));
        
        // Delete all instances for this schedule
        const q = query(
          collection(db, SCHEDULE_INSTANCES_COLLECTION),
          where('scheduleId', '==', scheduleId)
        );
        const querySnapshot = await getDocs(q);
        const batch = writeBatch(db);
        querySnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      } else {
        this.fallbackSchedules = this.fallbackSchedules.filter(s => s.id !== scheduleId);
        this.fallbackInstances = this.fallbackInstances.filter(i => i.scheduleId !== scheduleId);
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      throw new Error('Failed to delete schedule');
    }
  }

  // Update schedule instance status
  async updateScheduleInstance(instanceId: string, updates: Partial<ScheduleInstance>): Promise<void> {
    try {
      if (isFirebaseConfigured()) {
        const docRef = doc(db, SCHEDULE_INSTANCES_COLLECTION, instanceId);
        
        // Filter out undefined values before updating Firestore
        const updateData: any = {
          updatedAt: Timestamp.now()
        };

        // Only include fields that are not undefined
        Object.entries(updates).forEach(([key, value]) => {
          if (value !== undefined) {
            if (key === 'startDateTime' && value instanceof Date) {
              updateData[key] = Timestamp.fromDate(value);
            } else if (key === 'endDateTime' && value instanceof Date) {
              updateData[key] = Timestamp.fromDate(value);
            } else if (key === 'actualStartTime' && value instanceof Date) {
              updateData[key] = Timestamp.fromDate(value);
            } else if (key === 'actualEndTime' && value instanceof Date) {
              updateData[key] = Timestamp.fromDate(value);
            } else {
              updateData[key] = value;
            }
          }
        });

        await updateDoc(docRef, updateData);
      } else {
        const index = this.fallbackInstances.findIndex(i => i.id === instanceId);
        if (index !== -1) {
          this.fallbackInstances[index] = {
            ...this.fallbackInstances[index],
            ...updates,
            updatedAt: new Date()
          };
        }
      }
    } catch (error) {
      console.error('Error updating schedule instance:', error);
      throw new Error('Failed to update schedule instance');
    }
  }

  // Check for schedule conflicts
  async checkConflicts(schedule: Omit<Schedule, 'id' | 'createdAt' | 'updatedAt'>, excludeScheduleId?: string): Promise<ScheduleConflict[]> {
    const conflicts: ScheduleConflict[] = [];
    
    try {
      const instances = await this.getScheduleInstances(schedule.startDate, schedule.endDate || schedule.startDate, schedule.teacherId);
      
      for (const instance of instances) {
        // Skip instances from the schedule being edited
        if (excludeScheduleId && instance.scheduleId === excludeScheduleId) {
          continue;
        }
        
        // Check for time overlap
        const scheduleStart = new Date(`${schedule.startDate.toDateString()} ${schedule.startTime}`);
        const scheduleEnd = new Date(scheduleStart.getTime() + schedule.duration * 60000);
        
        if (scheduleStart < instance.endDateTime && scheduleEnd > instance.startDateTime) {
          conflicts.push({
            scheduleId: '',
            conflictingScheduleId: instance.scheduleId,
            conflictType: 'time_overlap',
            date: instance.startDateTime.toLocaleDateString()
          });
        }
      }
    } catch (error) {
      console.error('Error checking conflicts:', error);
    }
    
    return conflicts;
  }

  // Generate schedule instances based on recurrence pattern
  private async generateScheduleInstances(scheduleId: string): Promise<void> {
    const schedule = await this.getScheduleById(scheduleId);
    if (!schedule) return;

    const instances: Omit<ScheduleInstance, 'id' | 'createdAt' | 'updatedAt'>[] = [];
    const startDate = new Date(schedule.startDate);
    const endDate = schedule.endDate || new Date(startDate.getTime() + 365 * 24 * 60 * 60 * 1000); // Default to 1 year

    let currentDate = new Date(startDate);
    let occurrenceCount = 0;

    while (currentDate <= endDate && 
           (!schedule.recurrence.endAfterOccurrences || occurrenceCount < schedule.recurrence.endAfterOccurrences)) {
      
      if (this.shouldCreateInstance(currentDate, schedule.recurrence)) {
        const startTime = new Date(`${currentDate.toDateString()} ${schedule.startTime}`);
        const endTime = new Date(startTime.getTime() + schedule.duration * 60000);
        
        instances.push({
          scheduleId,
          lessonId: schedule.lessonId,
          teacherId: schedule.teacherId,
          className: schedule.className,
          startDateTime: startTime,
          endDateTime: endTime,
          status: 'scheduled'
        });
        
        occurrenceCount++;
      }
      
      currentDate = this.getNextDate(currentDate, schedule.recurrence);
    }

    // Save instances
    for (const instance of instances) {
      await this.createScheduleInstance(instance);
    }
  }

  // Regenerate instances for an existing schedule
  private async regenerateScheduleInstances(scheduleId: string): Promise<void> {
    // Delete existing instances
    if (isFirebaseConfigured()) {
      const q = query(
        collection(db, SCHEDULE_INSTANCES_COLLECTION),
        where('scheduleId', '==', scheduleId)
      );
      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);
      querySnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      await batch.commit();
    } else {
      this.fallbackInstances = this.fallbackInstances.filter(i => i.scheduleId !== scheduleId);
    }

    // Generate new instances
    await this.generateScheduleInstances(scheduleId);
  }

  // Create a single schedule instance
  private async createScheduleInstance(instance: Omit<ScheduleInstance, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> {
    const newInstance: ScheduleInstance = {
      ...instance,
      id: this.generateId(),
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      if (isFirebaseConfigured()) {
        // Filter out undefined values before saving to Firestore
        const instanceData: any = {
          scheduleId: newInstance.scheduleId,
          lessonId: newInstance.lessonId,
          teacherId: newInstance.teacherId,
          startDateTime: Timestamp.fromDate(newInstance.startDateTime),
          endDateTime: Timestamp.fromDate(newInstance.endDateTime),
          status: newInstance.status,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };

        // Only add optional fields if they have values
        if (newInstance.className) instanceData.className = newInstance.className;
        if (newInstance.actualStartTime) instanceData.actualStartTime = Timestamp.fromDate(newInstance.actualStartTime);
        if (newInstance.actualEndTime) instanceData.actualEndTime = Timestamp.fromDate(newInstance.actualEndTime);
        if (newInstance.attendance) instanceData.attendance = newInstance.attendance;
        if (newInstance.notes) instanceData.notes = newInstance.notes;

        await addDoc(collection(db, SCHEDULE_INSTANCES_COLLECTION), instanceData);
      } else {
        this.fallbackInstances.push(newInstance);
      }
    } catch (error) {
      console.error('Error creating schedule instance:', error);
    }
  }

  // Get schedule by ID
  private async getScheduleById(scheduleId: string): Promise<Schedule | null> {
    try {
      if (isFirebaseConfigured()) {
        const docSnap = await getDocs(query(collection(db, SCHEDULES_COLLECTION), where('__name__', '==', scheduleId)));
        if (!docSnap.empty) {
          const data = docSnap.docs[0].data();
          return {
            id: docSnap.docs[0].id,
            ...data,
            startDate: data.startDate.toDate(),
            endDate: data.endDate?.toDate(),
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          } as Schedule;
        }
      } else {
        return this.fallbackSchedules.find(s => s.id === scheduleId) || null;
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
    return null;
  }

  // Helper method to determine if an instance should be created for a given date
  private shouldCreateInstance(date: Date, recurrence: RecurrencePattern): boolean {
    switch (recurrence.type) {
      case 'none':
        return true;
      case 'daily':
        return true;
      case 'weekly':
        return recurrence.daysOfWeek?.includes(date.getDay()) || false;
      case 'monthly':
        return recurrence.dayOfMonth === date.getDate();
      default:
        return false;
    }
  }

  // Helper method to get the next date based on recurrence pattern
  private getNextDate(currentDate: Date, recurrence: RecurrencePattern): Date {
    const nextDate = new Date(currentDate);
    
    switch (recurrence.type) {
      case 'none':
        return new Date(nextDate.getTime() + 365 * 24 * 60 * 60 * 1000); // Far future
      case 'daily':
        nextDate.setDate(nextDate.getDate() + recurrence.interval);
        return nextDate;
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + 1);
        return nextDate;
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + recurrence.interval);
        return nextDate;
      default:
        return nextDate;
    }
  }

  // Export schedule data
  async exportScheduleData(teacherId: string, format: 'json' | 'csv'): Promise<string> {
    const schedules = await this.getSchedulesByTeacher(teacherId);
    const instances = await this.getScheduleInstances(
      new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // Next 30 days
      teacherId
    );

    if (format === 'json') {
      return JSON.stringify({ schedules, instances }, null, 2);
    } else {
      // CSV format
      const csvHeaders = 'Title,Lesson ID,Start Date,Start Time,Duration,Recurrence,Status\n';
      const csvRows = instances.map(instance => {
        const schedule = schedules.find(s => s.id === instance.scheduleId);
        return `"${schedule?.title || ''}","${instance.lessonId}","${instance.startDateTime.toDateString()}","${instance.startDateTime.toTimeString().slice(0, 5)}","${schedule?.duration || 0}","${schedule?.recurrence.type || 'none'}","${instance.status}"`;
      }).join('\n');
      return csvHeaders + csvRows;
    }
  }
}

export const scheduleService = new ScheduleService();
